'use client';

import { FC } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { WalletOwnerType } from '../../../types';
import {
  RegularPerson,
  RegularBuilding,
  RegularChevronRight,
} from '../../../../common/svgIcons';
import { HyperLinkButtonBase } from '../../../../common/components/HyperLinkButtonBase/HyperLinkButtonBase';
import { Divider } from '@bts-web/core-features/common';

interface WalletOwnerTypeScreenLabels {
  title: string;
  subtitle: string;
  learnMore: string;
  personTitle: string;
  personSubtitle: string;
  businessTitle: string;
  businessSubtitle: string;
}

interface WalletOwnerTypeScreenProps {
  onSelect: (ownerType: WalletOwnerType) => void;
  labels: WalletOwnerTypeScreenLabels;
}

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});

const headerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'extra_small',
  paddingY: 'small',
});

const questionStyles = css({
  color: 'neutrals.text_primary',
  fontSize: 'headline.large_semi_bold',
  fontWeight: 'headline.large_semi_bold',
  lineHeight: 'headline.large_semi_bold',
  letterSpacing: 'headline.large_semi_bold',
});

const subtitleStyles = css({
  color: 'neutrals.text_secondary',
  fontSize: 'body.medium',
  fontWeight: 'body.medium',
  lineHeight: 'body.medium',
  letterSpacing: 'body.medium',
});

const learnMoreStyles = css({
  textDecoration: 'underline',
  textUnderlineOffset: '4px',
  justifyContent: 'flex-start',
});

const optionButtonStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'small',
  width: '100%',
  paddingY: 'medium',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'start',
  borderBottom: '1px solid token(colors.neutrals.stroke_secondary)',
  '&:last-of-type': {
    borderBottom: 'none',
  },
  '&:active': {
    backgroundColor: 'neutrals.fill_focused',
  },
});

const optionTextGroupStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'extra_small_2',
  flex: 1,
});

const optionTitleStyles = css({
  color: 'neutrals.text_primary',
  fontSize: 'title.large',
  fontWeight: 'title.large',
  lineHeight: 'title.large',
  letterSpacing: 'title.large',
});

const optionSubtitleStyles = css({
  color: 'neutrals.text_secondary',
  fontSize: 'body.medium',
  fontWeight: 'body.medium',
  lineHeight: 'body.medium',
  letterSpacing: 'body.medium',
});

const WalletOwnerTypeScreen: FC<WalletOwnerTypeScreenProps> = ({
  onSelect,
  labels,
}) => {
  return (
    <div className={containerStyles}>
      <div className={headerStyles}>
        <p className={questionStyles}>{labels.title}</p>

        <p className={subtitleStyles}>{labels.subtitle}</p>

        <HyperLinkButtonBase
          to="#"
          visual="ghost"
          size="medium"
          className={learnMoreStyles}
          noPadding
        >
          {labels.learnMore}
        </HyperLinkButtonBase>
      </div>

      <button
        type="button"
        className={optionButtonStyles}
        onClick={() => onSelect(WalletOwnerType.NaturalPerson)}
        data-testid="wallet-owner-type-person"
      >
        <RegularPerson size={24} />

        <div className={optionTextGroupStyles}>
          <span className={optionTitleStyles}>{labels.personTitle}</span>

          <span className={optionSubtitleStyles}>{labels.personSubtitle}</span>
        </div>

        <RegularChevronRight size={20} />
      </button>

      <Divider />

      <button
        type="button"
        className={optionButtonStyles}
        onClick={() => onSelect(WalletOwnerType.LegalEntity)}
        data-testid="wallet-owner-type-business"
      >
        <RegularBuilding size={24} />

        <div className={optionTextGroupStyles}>
          <span className={optionTitleStyles}>{labels.businessTitle}</span>

          <span className={optionSubtitleStyles}>
            {labels.businessSubtitle}
          </span>
        </div>

        <RegularChevronRight size={20} />
      </button>
    </div>
  );
};

export { WalletOwnerTypeScreen };

export type { WalletOwnerTypeScreenLabels };
